import { createFileRoute } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { toastManager } from "../components/ui";
import { SettingsHeader } from "../components/SettingsHeader";
import {
  useEmailPreferences,
  useUpdateEmailPreference,
  type EmailCategory,
} from "../lib/useEmailPreferences";
import { useSessionEmail } from "../lib/useSessionUser";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/settings/notifications")({
  component: NotificationSettings,
});

const EMAIL_SWITCHES: {
  key: EmailCategory;
  label: string;
  hint: string;
}[] = [
  {
    key: "job_notifications",
    label: "When a mascot is ready",
    hint: "An email when a draft, version or variation you started finishes or fails.",
  },
  {
    key: "credit_reminders",
    label: "Before credits expire",
    hint: "A reminder a few days before unused paid credits run out.",
  },
  {
    key: "product_updates",
    label: "Product news and offers",
    hint: "Occasional emails about new features and deals. Off unless you turn it on.",
  },
];

/** Notifications start on; product news is opt-in and starts off. */
const DEFAULT_ON: Record<EmailCategory, boolean> = {
  job_notifications: true,
  credit_reminders: true,
  product_updates: false,
};

/** On/off switches for the notification e-mails (ADR 0028). */
function NotificationSettings() {
  const email = useSessionEmail();
  const prefs = useEmailPreferences();
  const update = useUpdateEmailPreference();

  function toggle(key: EmailCategory, value: boolean) {
    update.mutate(
      { [key]: value },
      {
        onError: () =>
          toastManager.add({
            type: "error",
            title: "Could not save your email settings",
          }),
      }
    );
  }

  return (
    <div>
      <SettingsHeader title="Notifications">
        Emails about your mascots and credits, and optional product news.
      </SettingsHeader>
      <div className="tactile p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <Mail
            className="mt-0.5 size-5 shrink-0 text-lilac-600"
            aria-hidden="true"
          />
          <p className="text-sm font-semibold text-ink-soft">
            We email {email ?? "you"} about your own account. Product news and
            offers only reach you if you turn them on below.
          </p>
        </div>
        {prefs.data?.suppressed && (
          <p className="mt-4 rounded-xl border-2 border-line-strong bg-paper p-3 text-sm font-semibold text-ink">
            Emails to this address bounced or were reported as spam, so we
            stopped sending them. Write to support if you want them back.
          </p>
        )}
        <ul className="mt-4 divide-y-2 divide-line">
          {EMAIL_SWITCHES.map((s) => {
            const on = prefs.data?.[s.key] ?? DEFAULT_ON[s.key];
            const pending =
              update.isPending && update.variables?.[s.key] !== undefined;
            return (
              <li
                key={s.key}
                className="flex items-center justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p
                    className="text-sm font-bold text-ink"
                    id={`email-${s.key}`}
                  >
                    {s.label}
                  </p>
                  <p className="text-xs font-semibold text-ink-soft">
                    {s.hint}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-labelledby={`email-${s.key}`}
                  disabled={prefs.isLoading || pending}
                  onClick={() => toggle(s.key, !on)}
                  className={cn(
                    "relative h-7 w-12 shrink-0 rounded-full border-2 transition-colors disabled:opacity-60",
                    on
                      ? "border-lilac-600 bg-lilac-500"
                      : "border-line-strong bg-paper"
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 size-5 rounded-full bg-white shadow transition-[left]",
                      on ? "left-[22px]" : "left-0.5"
                    )}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
